import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import deviceIconUrl from 'assets/bf-images/device_icon.svg';
import { deviceDateFormat } from 'App/ui-utils';
import { useTranslation } from 'react-i18next';
import { Card } from 'react-bootstrap';
import { IBFProductVariable, ILeadData } from 'Services/widgets/interfaces';
import { IProductDataGM, useGarantieMaxActions } from '../GarantieMax';
import { FIELD_TYPE_BOOL, isObject, FIELD_TYPE_DATETIME } from '../../bf-hooks';
import { blankLink } from 'config';
import { isEmpty } from 'lodash';
import { ProductFieldTypeEntity } from 'Services/widgets/enums';

export interface IViewTariffSubStep1Props {
  lead: ILeadData;
  productData: IProductDataGM;
}

export const ViewTariffSubStep1: React.FC<IViewTariffSubStep1Props> = ( { lead, productData } ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const actions = useGarantieMaxActions();
  const { subStep1DeviceItems } = lead;

  const getValueFactorField = React.useCallback( ( variable: IBFProductVariable, insuredId: number ) => {
    if ( productData === null ) {
      return;
    }

    const selectedVal = lead.subStep1TariffData![`${variable.name}_${insuredId}`];
    if ( isObject( selectedVal ) && selectedVal['name'] ) {
      return selectedVal['name'];
    }

    if ( selectedVal ) {
      const findFactor = productData.factors.find( ( f ) => f.id === variable.typeId )!;
      if ( findFactor ) {
        const factorInfo = findFactor?.values.find( ( f ) => f.key === selectedVal )!;
        return factorInfo?.name;
      }
    }

    return '-';
  }, [ lead.subStep1TariffData, productData ] );

  const getValueBaseField = React.useCallback( ( variable: IBFProductVariable, insuredId: number ) => {
    const selectedVal = lead.subStep1TariffData![`${variable.name}_${insuredId}`];
    if ( variable.typeId === FIELD_TYPE_BOOL && selectedVal ) {
      return selectedVal ? t( 'bookingFunnel.tariff.yes' ) : t( 'bookingFunnel.tariff.no' );
    }

    if ( variable.typeId === FIELD_TYPE_DATETIME && selectedVal ) {
      return dayjs( selectedVal ).format( deviceDateFormat );
    }

    const resVal: string = selectedVal ? selectedVal.toString() : '';
    return !isEmpty( resVal ) ? resVal : '-';
  }, [ lead.subStep1TariffData, t ] );

  const onClickEditStep = React.useCallback ( ( e ) => {
    e.preventDefault();
    actions.goToStep( 'tarif', false );
  }, [ actions ] );

  return (
    <Fragment>
      { productData !== null && (
        <Card className="bg-none">
          <Card.Body>
            <div className="d-flex justify-content-between">
              <h3>{ t( 'bookingFunnel.summary.services' ) }</h3>
              <a
                href={ blankLink }
                onClick={ ( e ) => { onClickEditStep( e ); } }
                title={ t( 'base:forms.edit' ) }
              >
                { t( 'base:forms.edit' ) }
              </a>
            </div>
            <hr className="mt-3 mb-3"></hr>
            <div>
              { subStep1DeviceItems && subStep1DeviceItems.map( ( device, idx ) => (
                <div key={ idx } className="mb-4">
                  <h3 className="my-3 f-16 font-weight-normal">
                    <img className="mr-2 mb-1" src={ deviceIconUrl } alt={ `Device Icon ${idx}` } />
                    { t( 'bookingFunnel.tariff.deviceTitle', { idx: idx + 1 } ) }
                  </h3>
                  { device.insuredId !== null && productData.insuredObjects
                    .filter( ( insured ) => insured.id === device.insuredId )
                    .map( ( insured, index ) => (
                      <Fragment key={ index }>
                        { insured.productFields && insured.productFields
                          .map( ( variable, itemIdx ) => (
                            <Fragment key={ itemIdx }>
                              <p className="ml-4 pl-1">{ `${variable.label}: ` }
                                { variable.typeEntity === ProductFieldTypeEntity.SystemProductFieldType && (
                                  <Fragment>{ getValueBaseField( variable, device.insuredId! ) }</Fragment>
                                ) }
                                { variable.typeEntity === ProductFieldTypeEntity.ProductFactorType && (
                                  <Fragment>{ getValueFactorField( variable, device.insuredId! ) }</Fragment>
                                ) }
                              </p>
                            </Fragment>
                          ) ) }
                      </Fragment>
                    ) ) }
                </div>
              ) ) }
            </div>
          </Card.Body>
        </Card>
      ) }
    </Fragment>
  );
};
